const STATUS_I18N_KEYS = {
  STRONG_BUY: "aiselect.status.strongBuy",
  NEUTRAL: "aiselect.status.neutral",
  NEGATIVE: "aiselect.status.negative",
};

const SIGNAL_LABEL_I18N_KEYS = {
  "strong positive": "aiselect.status.strongPositive",
  "strong buy": "aiselect.status.strongBuy",
  positive: "aiselect.status.positive",
  buy: "aiselect.status.positive",
  neutral: "aiselect.status.neutral",
  "strong negative": "aiselect.status.negative",
  "strong sell": "aiselect.status.negative",
  negative: "aiselect.status.negative",
  sell: "aiselect.status.negative",
};

function translateWithFallback(t, key, fallback) {
  if (typeof t !== "function") return fallback;
  const text = t(key);
  return text && text !== key ? text : fallback;
}

/** 接口 aiComment.status → 列表/卡片用的文案（与 getSignalDirection 解析一致） */
export function aiStatusToSignalLabel(status, t) {
  const s = String(status || "").toUpperCase();
  if (s === "STRONG_BUY") return translateWithFallback(t, "aiselect.status.strongPositive", "Strong Positive");
  if (s === "NEUTRAL") return translateWithFallback(t, "aiselect.status.neutral", "Neutral");
  if (s === "NEGATIVE") return translateWithFallback(t, "aiselect.status.negative", "Negative");
  return translateWithFallback(t, "aiselect.status.positive", "Positive");
}

/** 详情页 AI Comment 标题侧展示 */
export function aiStatusToShortLabel(status, t) {
  const s = String(status || "").toUpperCase();
  const key = STATUS_I18N_KEYS[s] || "aiselect.status.positive";
  const fallbacks = {
    STRONG_BUY: "Strong Buy",
    NEUTRAL: "Neutral",
    NEGATIVE: "Negative",
  };
  return translateWithFallback(t, key, fallbacks[s] || "Positive");
}

/** mock/API 英文 signal 文案 → 多语言展示 */
export function translateSignalLabel(signalText, t) {
  const text = String(signalText || "").trim();
  if (!text) return translateWithFallback(t, "aiselect.status.positive", "Positive");
  const key = SIGNAL_LABEL_I18N_KEYS[text.toLowerCase()];
  return key ? translateWithFallback(t, key, text) : text;
}

/** ai_comment.signal / scoreSignal 兜底文案 → 详情页色调（收敛为三档） */
export function signalToToneKey(signalText) {
  const text = String(signalText || "").toLowerCase();
  if (text.includes("negative") || text.includes("sell")) return "negative";
  if (text.includes("neutral")) return "neutral";
  return "positive";
}

/** Demo scoreSignal：5–10 分制 → 英文信号文案（仅 signal 缺失时兜底） */
export function scoreToSignalLabel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Neutral";
  if (n >= 9) return "Strong Buy";
  if (n >= 7.5) return "Buy";
  if (n >= 6) return "Neutral";
  if (n >= 5.5) return "Sell";
  return "Strong Sell";
}

const SCORE_SIGNAL_I18N_KEYS = {
  "Strong Buy": "aiselect.status.strongBuy",
  Buy: "aiselect.status.positive",
  Neutral: "aiselect.status.neutral",
  Sell: "aiselect.status.negative",
  "Strong Sell": "aiselect.status.negative",
};

/** 将 ai_rating 分数规范为 demo 同款 5–10 分（combined_score 0–100 时按 5 + score/20） */
export function normalizeDisplayScoreForSignal(aiRating) {
  if (!aiRating || typeof aiRating !== "object") return 5;
  const raw = aiRating.totalScore ?? aiRating.combinedScore ?? aiRating.sentiment;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 5;
  if (n > 10) return Math.max(5, Math.min(10, 5 + n / 20));
  return Math.max(5, Math.min(10, n));
}

/**
 * AI Comment 标题 + 雷达图中心信号（与 demo ref-signal 一致）：
 * 1) display_snapshot.ai_comment.signal
 * 2) 否则 scoreSignal(ai_rating.total_score → combinedScore → sentiment)
 */
export function resolveAiCommentDisplayState({ signal, aiRating }, t) {
  const trimmed = String(signal ?? "").trim();
  if (trimmed) {
    return {
      label: translateSignalLabel(trimmed, t),
      key: signalToToneKey(trimmed),
      source: "signal",
    };
  }
  const displayScore = normalizeDisplayScoreForSignal(aiRating);
  const fallbackText = scoreToSignalLabel(displayScore);
  const i18nKey = SCORE_SIGNAL_I18N_KEYS[fallbackText] || "aiselect.status.neutral";
  return {
    label: translateWithFallback(t, i18nKey, fallbackText),
    key: signalToToneKey(fallbackText),
    source: "score",
  };
}

/** @deprecated 请使用 resolveAiCommentDisplayState */
export function resolveAiStateFromSignal(signal, t) {
  return resolveAiCommentDisplayState({ signal, aiRating: null }, t);
}

/** 评分轴比例：接口 0–100 或 0–10 均归一化到 [0, 1] */
export function normalizeRatingAxisRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 10) return Math.min(1, n / 100);
  return Math.min(1, n / 10);
}

/** 评分展示：接口原样数值 */
export function ratingToDisplayScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return String(n);
}

/**
 * 综合评分：优先 `combinedScore`（与后端一致），缺省时用 `sentiment`。
 * 展示为接口原始值。
 */
export function comprehensiveScoreToDisplay(aiRating) {
  if (!aiRating || typeof aiRating !== "object") return "—";
  const raw = aiRating.combinedScore ?? aiRating.sentiment;
  return ratingToDisplayScore(raw);
}

/** 趋势文案：接口返回什么展示什么 */
export function trendToLabel(trend) {
  if (trend == null || trend === "") return "—";
  return String(trend);
}

export function formatCompactNumber(n) {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(2)}k`;
  return String(Math.round(num));
}

/** Tab 括号内数量：原样整数，不做 k/M 缩写 */
export function formatTabCount(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";
  return String(Math.round(num));
}
